<script>
    import { onMount, onDestroy } from "svelte";
    import Router from "svelte-spa-router";
    import { wrap } from "svelte-spa-router/wrap";
    import { push } from "svelte-spa-router";

    import Home from "./routes/Home.svelte";
    import Login from "./routes/Login.svelte";
    import NotFound from "./routes/NotFound.svelte";
    import FlashcardModeSelection from "./routes/FlashcardModeSelection.svelte";
    import FlashcardDeckSpaced from "./routes/FlashcardDeckSpaced.svelte";
    import FlashcardDeckIntensive from "./routes/FlashcardDeckIntensive.svelte";
    import Quiz from "./routes/Quiz.svelte";
    import Article from "./routes/Article.svelte";
    import Toc from "./routes/Toc.svelte";
    import BugReports from "./routes/BugReports.svelte";

    import StatusBar from "./lib/components/StatusBar.svelte";

    import {
        auth,
        resources as resourcesStore,
        progress as progressStore,
        syncStatus,
        pushNotificationsStore,
        theme,
        darkMode,
        fontPreference,
    } from "./stores.js";
    import { registerServiceWorker } from "./lib/registerSW.js";
    import { initDB } from "./db/index.js";
    import { syncManager } from "./lib/sync.js";
    import { pushNotifications } from "./lib/pushNotifications.js";
    import axios, { setupAxiosInterceptors } from "@edu-quizz/shared/lib/axios";

    let ready = false;
    let csrfRefreshInterval;

    // Auth guard for protected routes
    function authGuard(detail) {
        console.log(
            "Auth guard check - isAuthenticated:",
            $auth.isAuthenticated,
            "loading:",
            $auth.loading,
        );

        // If still loading, don't allow access yet
        if ($auth.loading) {
            console.log("Auth guard - still loading, blocking access");
            return false;
        }

        if (!$auth.isAuthenticated) {
            console.log(
                "Auth guard - NOT authenticated, redirecting to /login",
            );
            // Force redirect using push
            setTimeout(() => push("/login"), 0);
            return false;
        }
        console.log("Auth guard - allowing access");
        return true;
    }

    const routes = {
        "/": wrap({
            component: Home,
            conditions: [authGuard],
        }),
        "/flashcard/:id": wrap({
            component: FlashcardModeSelection,
            conditions: [authGuard],
        }),
        "/flashcard/:id/spaced": wrap({
            component: FlashcardDeckSpaced,
            conditions: [authGuard],
        }),
        "/flashcard/:id/intensive": wrap({
            component: FlashcardDeckIntensive,
            conditions: [authGuard],
        }),
        "/quiz/:id": wrap({
            component: Quiz,
            conditions: [authGuard],
        }),
        "/article/:id": wrap({
            component: Article,
            conditions: [authGuard],
        }),
        "/toc/:id": wrap({
            component: Toc,
            conditions: [authGuard],
        }),
        "/bug-reports": wrap({
            component: BugReports,
            conditions: [authGuard],
        }),
        "/bug-reports/new": wrap({
            component: BugReports,
            conditions: [authGuard],
        }),
        "/login": Login,
        "*": NotFound,
    };

    onMount(async () => {
        try {
            // Initialize database first
            const db = await initDB();

            // Setup axios interceptors for 401 handling (pass db instance)
            setupAxiosInterceptors(push, db);

            // Get CSRF cookie at app startup
            try {
                await axios.get("/sanctum/csrf-cookie");
            } catch (error) {
                console.warn("Failed to get CSRF cookie:", error);
            }

            // Refresh CSRF token periodically (every 30 minutes)
            // This helps prevent 419 errors for long-lived sessions
            csrfRefreshInterval = setInterval(async () => {
                try {
                    await axios.get("/sanctum/csrf-cookie");
                    console.log("CSRF token refreshed");
                } catch (error) {
                    console.warn("Failed to refresh CSRF token:", error);
                }
            }, 30 * 60 * 1000); // 30 minutes

            // Initialize auth store from database
            await auth.init();

            // Initialize theme store
            await theme.init();

            // Initialize dark mode
            await darkMode.init();

            // Initialize font preference
            await fontPreference.init();

            // IMPORTANT: Set ready AFTER auth is initialized
            // This ensures the router and auth guards have the correct auth state
            ready = true;

            // Register service worker (can happen after ready)
            const workbox = await registerServiceWorker();

            // Initialize push notifications
            if (workbox) {
                // Get the actual ServiceWorkerRegistration from Workbox
                const swRegistration = await navigator.serviceWorker.ready;

                await pushNotifications.init(swRegistration);
                await pushNotificationsStore.init(pushNotifications);

                // Listen for navigation messages from service worker (for notification clicks)
                if ("serviceWorker" in navigator) {
                    navigator.serviceWorker.addEventListener(
                        "message",
                        (event) => {
                            if (event.data && event.data.type === "NAVIGATE") {
                                push(event.data.url);
                            }
                        },
                    );
                }
            }

            // Load resources and progress if authenticated
            if ($auth.isAuthenticated) {
                await resourcesStore.load();
                await progressStore.load();
                await syncStatus.updatePendingCount();

                // Start auto-sync
                syncManager.startAutoSync(15); // Every 15 minutes
            }
        } catch (error) {
            console.error("Failed to initialize app:", error);
            ready = true; // Show app anyway
        }
    });

    onDestroy(() => {
        // Clean up CSRF refresh interval
        if (csrfRefreshInterval) {
            clearInterval(csrfRefreshInterval);
        }
    });
</script>

{#if ready}
    <main>
        {#if $auth.isAuthenticated}
            <StatusBar />
        {/if}
        <Router {routes} />
    </main>
{:else}
    <div class="loading">
        <div class="spinner-large"></div>
        <p>Chargement...</p>
    </div>
{/if}

<style>
    main {
        min-height: 100vh;
    }

    nav {
        display: flex;
        justify-content: flex-start;
        align-items: center;
        margin-bottom: 2rem;
        padding: 1rem;
        background-color: rgba(255, 255, 255, 0.05);
        border-radius: 8px;
    }

    .nav-links {
        display: flex;
        gap: 1rem;
    }

    nav a {
        padding: 0.5rem 1rem;
        text-decoration: none;
        color: var(--primary);
        transition: all 0.3s;
        border-radius: 4px;
    }

    nav a:hover {
        color: var(--primary-hover);
        background-color: rgba(255, 255, 255, 0.1);
    }

    .loading {
        display: flex;
        flex-direction: column;
        align-items: center;
        justify-content: center;
        min-height: 100vh;
        gap: 1rem;
    }

    .spinner-large {
        width: 48px;
        height: 48px;
        border: 4px solid rgba(55, 65, 81, 0.2);
        border-top-color: var(--accent-gradient-start);
        border-radius: 50%;
        animation: spin 1s linear infinite;
    }

    @keyframes spin {
        to {
            transform: rotate(360deg);
        }
    }

    .loading p {
        color: #6b7280;
        font-size: 1rem;
    }

    @media (max-width: 640px) {
        nav {
            flex-direction: column;
            gap: 1rem;
        }

        .nav-links {
            width: 100%;
            justify-content: center;
        }
    }
</style>
